import _ from 'lodash';
import moment from 'moment';
import { useState } from 'react';
import {
  CHANGE_EVENT_TYPE_OPTIONS,
  PREVENT_AFIB_OVERRIDE_EVENT_LIST,
  RHYTHM_EVENT_LIST_CAN_EDIT,
  RUN_EVENT_CHANGE_ALLOWED_TYPES,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_BEAT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  minumum3BeatsType,
  typesPersistEvent,
} from '@/shared/config';
import { getEventDuration, getEventDurationInSecond } from '@/shared/lib/aiUtils';
import { useMergeState } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE,
  ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE,
  ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE,
  ADD_EVENT_UNDER_3_BEATS_ERROR_MESSAGE,
  CHANGE_EVENT_TYPE_RUN_UNDER_3_BEATS_ERROR_MESSAGE,
  UPDATE_EVENT_NO_DATA_ERROR_MESSAGE,
  generateChangeEventExistErrorMessage,
  getBeatCountAddUpdateEvents,
  getEventBothSideAddUpdateEvents,
  isCheckContinueData,
  updateEventChangeAndNew,
  updateEventChangeType,
} from '@/entities/event';
import { getDataRange, isRangesOverlap } from '@/shared/lib/timeRange';
import {
  selectActiveTab,
  selectBeatChanges,
  selectEventChanges,
  selectEventNews,
  useReportStore,
} from '@/entities/report';
import type { ChangeEventTypeProps } from '.';

/**
 * Drives the dialog that retypes one or several selected ECG event strips. Wires report-store reads and writes for event changes, new events, beat changes and the active tab, holds the chosen type plus validation errors in merged local state, and exposes the cancel, select and save handlers along with the tab-filtered option list.
 *
 * @param props - Dialog props supplying the selected strip, the multi-selection, the Recoil key, the ECG data map, study and profile ids, timezone offset and the cancel callback.
 * @returns An object carrying state, isLoading, onChangeEvent, onClickCancel, onClickChangeEventType and getAvailableChangeEventTypeOptions.
 */
const useChangeEventType = ({
  selectedStrip,
  selectedMultipleStrip,
  keyRecoil,
  onCancel,
  ecgDataMap,
  studyId,
  profileId,
  timezoneOffset,
}: ChangeEventTypeProps) => {
  /**
   * Reads the list of newly added ECG events staged for this Recoil key. Runs selectEventNews against a getState snapshot rather than subscribing, so freshly pushed events are visible immediately.
   *
   * @returns The current list of new events.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the staged list of newly created events for this Recoil key. Forwards the value to the store action with the key already bound.
   *
   * @param v - The new list of created events, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the pending edits to existing ECG events for this Recoil key. Runs selectEventChanges against a getState snapshot so validation sees writes made earlier in the same handler.
   *
   * @returns The current list of changed events.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending edits to existing events for this Recoil key back into the report store. Forwards the value to the store action with the key already bound.
   *
   * @param v - The new list of changed events, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Writes the combined event options snapshot that undo and redo replay from. Forwards the value to the store action with the key already bound.
   *
   * @param v - The new event options object, or an updater over the current one.
   * @returns The value returned by the store action.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);
  /**
   * Reads the pending beat edits held for this Recoil key in the report store. Runs the selectBeatChanges selector over a getState snapshot, bypassing the subscription so the caller sees the latest writes.
   *
   * @returns The current list of beat changes for the report key.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the currently active report tab for this Recoil key straight off the report store. Calls the selectActiveTab selector against a one-shot getState snapshot so the value is fresh rather than subscribed.
   *
   * @returns The active tab identifier for the current report key.
   */
  const getActiveTab = () => selectActiveTab(keyRecoil)(useReportStore.getState());

  const [isLoading, setIsLoading] = useState(false);

  const [state, setState] = useMergeState<{
    eventType: any;
    errorObject?: any;
    isSaving?: boolean;
  }>({
    eventType: CHANGE_EVENT_TYPE_OPTIONS[0],
  });

  /**
   * Records the technician's pick in the change-event-type form. Merges a single keyed field into the local component state.
   *
   * @param name - State key being written.
   * @param option - New value for that key, typically the selected event type option.
   * @returns Nothing.
   */
  const onChangeEvent = (name: string, option: any) => {
    setState({ [name]: option });
  };

  /**
   * Abandons the pending event type change and closes the dialog. Clears the validation errors, resets the type selector back to the first option, and invokes the onCancel callback passed in props.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    setState({ errorObject: {}, eventType: CHANGE_EVENT_TYPE_OPTIONS[0] });
    onCancel();
  };

  /**
   * Trims an existing event down to the part not covered by the incoming event and keeps it as a link candidate. Shortens the overlapping edge, counts the beats remaining in the trimmed range, discards the remnant when it holds no beats or a single beat under ten seconds, and otherwise pushes the recomputed event onto the link list.
   *
   * @param event - The existing event overlapping the new one, itself left unmodified.
   * @param newEvent - The incoming event whose range carves the overlap out.
   * @param linkEvents - Accumulator the trimmed remnant is pushed onto.
   * @param localBeatChanges - Locally added or deleted beats folded into the beat count for the trimmed range.
   * @returns A promise resolving once the trimmed remnant has been evaluated and, when worth keeping, appended to the link list.
   */
  const handlePartialOverlap = async (
    event: any,
    newEvent: any,
    linkEvents: any[],
    localBeatChanges: any,
  ) => {
    let currentEventStart = event.start;
    let currentEventStop = event.stop;

    if (event.start < newEvent.start && newEvent.start < event.stop) {
      currentEventStop = newEvent.start;
    }

    if (event.start < newEvent.stop && newEvent.stop < event.stop) {
      currentEventStart = newEvent.stop;
    }

    const { mergeBeatCount } = await getBeatCountAddUpdateEvents({
      start: currentEventStart,
      stop: currentEventStop,
      samplingFrequency: ecgDataMap.samplingFrequency,
      localBeatChanges,
      ecgDataMap,
    });

    const newDurationInSec = getEventDurationInSecond(currentEventStart, currentEventStop);

    if (mergeBeatCount === 0 || (mergeBeatCount === 1 && newDurationInSec < 10)) {
      return;
    }

    linkEvents.push({
      ...event,
      start: currentEventStart,
      stop: currentEventStop,
      duration: getEventDuration(currentEventStart, currentEventStop),
      canChangeDuration: true,
    });
  };

  /**
   * Picks the human-readable conflict message for an event type change that collides with an AFIB or ARTIFACT rhythm event. Returns the add-AFIB-to-ARTIFACT message when the new event is AFIB over an ARTIFACT, the reverse message for ARTIFACT over AFIB, and null when the pair raises no conflict.
   *
   * @param newEvent - The event being created or retyped, whose type drives the conflict check.
   * @param event - The existing overlapping event the new one is compared against.
   * @param eventType - The effective type of the existing event, already resolved from oldType for deleted beats.
   * @returns The matching error message string, or null when the combination is allowed.
   */
  const generateErrorMessage = (newEvent: any, event: any, eventType: any) => {
    if (
      newEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
      eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
    ) {
      return ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE;
    }

    if (
      newEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT &&
      eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
    ) {
      return ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE;
    }

    return null;
  };

  /**
   * Surfaces a validation failure for the event time range and unblocks the form. Raises an error toast with the given message, marks both startTime and endTime as invalid in a clone of the error object, and clears the saving flag.
   *
   * @param toastMessage - Message shown in the error toast; defaults to an empty string.
   * @returns Nothing.
   */
  const handleSetError = (toastMessage = '') => {
    toastrError(toastMessage, 'Error');
    const clonedErrorObject = { ...state.errorObject };
    _.assign(clonedErrorObject, {
      startTime: 'Invalid value',
      endTime: 'Invalid value',
    });
    setState({
      errorObject: clonedErrorObject,
      isSaving: false,
    });
  };

  /**
   * Walks every neighbouring event to work out how the incoming event overlaps them and what that implies. Skips pairs barred by the AFIB override list, links or merges same-type overlaps and widens the new event's range accordingly, records an error event when the new event sits inside an existing one, and hands partial overlaps of editable rhythm types to the trimming path.
   *
   * @param combinedEvents - Persisted events for the range merged with local changes and additions.
   * @param newEvent - The incoming event, whose start and stop may be widened to swallow same-type neighbours.
   * @param localBeatChanges - Locally added or deleted beats used when counting beats in trimmed remnants.
   * @returns A promise resolving to an object carrying linkEvents, errorEvent and errorMessages.
   */
  const processEvent = async ({ combinedEvents, newEvent, localBeatChanges }: any) => {
    const newEventStart = new Date(newEvent.start).getTime();
    const newEventStop = new Date(newEvent.stop).getTime();

    const linkEvents: any[] = [];
    let errorEvent;
    let errorMessages;

    combinedEvents.forEach(async (event: any) => {
      const eventType = event.type === TYPE_BEAT_ENUM.DELETED ? event.oldType : event.type;

      // Skip conflicts with AFIB rules
      if (
        (PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(eventType) &&
          newEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB) ||
        (PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(newEvent.type) &&
          eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB)
      ) {
        return;
      }

      const eventStart = new Date(event.start).getTime();
      const eventStop = new Date(event.stop).getTime();

      if (isRangesOverlap(eventStart, eventStop, newEventStart, newEventStop)) {
        const isSameType = eventType === newEvent.type;
        const isNewEventContainOldEvent = eventStart >= newEventStart && eventStop <= newEventStop;
        const isNewEventInsideOldEvent = newEventStart >= eventStart && newEventStop <= eventStop;

        if (isSameType) {
          if (isNewEventContainOldEvent) {
            if (
              [TYPE_RHYTHM_EVENT_ENUM.AFIB, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT].includes(eventType)
            ) {
              linkEvents.push(event);
            }
          } else if (isNewEventInsideOldEvent) {
            errorEvent = event;
          } else {
            if (
              [TYPE_RHYTHM_EVENT_ENUM.AFIB, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT].includes(eventType) ||
              event.isNew
            ) {
              linkEvents.push(event);
            }
            newEvent.start = new Date(Math.min(eventStart, newEventStart)).toISOString();
            newEvent.stop = new Date(Math.max(eventStop, newEventStop)).toISOString();
          }
        } else if (RHYTHM_EVENT_LIST_CAN_EDIT.includes(eventType)) {
          if (isNewEventContainOldEvent) {
            if (
              [TYPE_RHYTHM_EVENT_ENUM.AFIB, TYPE_RHYTHM_EVENT_ENUM.ARTIFACT].includes(eventType)
            ) {
              linkEvents.push(event);
            }
          } else if (isNewEventInsideOldEvent) {
            errorMessages = generateErrorMessage(newEvent, event, eventType);
            errorEvent = event;
          } else {
            await handlePartialOverlap(event, newEvent, linkEvents, localBeatChanges);
          }
        }
      }
    });

    return { linkEvents, errorEvent, errorMessages };
  };

  /**
   * Validates that an event can be retyped over its time range before the change is staged. Clamps the range to available ECG data, aborts when the range is empty or discontinuous, fetches neighbouring persisted events and merges local changes, rejects an overlapping AFIB event when converting between SVE and VE runs, delegates the remaining overlap checks, and on success stamps startTimeEpoch and duration on the event.
   *
   * @param newEvent - The event carrying the requested new type, mutated in place with the clamped start, stop, startTimeEpoch and duration.
   * @returns A promise resolving to true when the retype is allowed, or false after an error toast has been raised.
   */
  const checkChangeTypeEvent = async (newEvent: any) => {
    const momentStart = moment(newEvent.start);
    const momentStop = moment(newEvent.stop);
    const dataRange = getDataRange(ecgDataMap, momentStart, momentStop);

    if (_.isEmpty(dataRange)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return false;
    }

    const startData = dataRange[0].start;
    const stopData = dataRange[dataRange.length - 1].stop;

    newEvent.start = startData > momentStart ? new Date(startData).toISOString() : newEvent.start;
    newEvent.stop = stopData < momentStop ? new Date(stopData).toISOString() : newEvent.stop;

    if (!isCheckContinueData(dataRange, newEvent)) {
      handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
      return false;
    }

    const localBeatChanges = getBeatChanges().filter(
      (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
    );

    const holterEventsFilter = {
      studyId,
      profileId,
      types: typesPersistEvent,
      startSearchDate: newEvent.start,
      stopSearchDate: newEvent.stop,
    };

    const dataFormat = await getEventBothSideAddUpdateEvents(holterEventsFilter);
    const combinedEvents = updateEventChangeAndNew(
      dataFormat,
      getEventChanges(),
      getEventNews(),
      holterEventsFilter.startSearchDate,
      holterEventsFilter.stopSearchDate,
      typesPersistEvent,
      true,
    );

    // Check overlap AFIB event when changing to SVE_RUN or VE_RUN

    const isVERunToSBeats =
      (newEvent.oldType === TYPE_BEAT_EVENT_ENUM.VE_RUN ||
        newEvent.oldType === TYPE_RHYTHM_EVENT_ENUM.VT) &&
      [TYPE_BEAT_EVENT_ENUM.SVE_RUN, TYPE_RHYTHM_EVENT_ENUM.SVT].includes(newEvent.type);
    const isSVERunToVBeats =
      (newEvent.oldType === TYPE_BEAT_EVENT_ENUM.SVE_RUN ||
        newEvent.oldType === TYPE_RHYTHM_EVENT_ENUM.SVT) &&
      [TYPE_BEAT_EVENT_ENUM.VE_RUN, TYPE_RHYTHM_EVENT_ENUM.VT].includes(newEvent.type);

    if (isVERunToSBeats || isSVERunToVBeats) {
      const newEventStart = new Date(newEvent.start).getTime();
      const newEventStop = new Date(newEvent.stop).getTime();

      const overlapAfibEvent = _.find(combinedEvents, (event) => {
        if (event.type === TYPE_BEAT_ENUM.DELETED) return false;
        if (event.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB) return false;

        const eventStart = new Date(event.start).getTime();
        const eventStop = new Date(event.stop).getTime();
        return isRangesOverlap(eventStart, eventStop, newEventStart, newEventStop);
      });

      if (overlapAfibEvent) {
        handleSetError(generateChangeEventExistErrorMessage(overlapAfibEvent, timezoneOffset));
        return false;
      }
    }

    const { errorEvent, errorMessages } = await processEvent({
      combinedEvents,
      newEvent,
      localBeatChanges,
    });

    if (errorEvent) {
      handleSetError(generateChangeEventExistErrorMessage(errorEvent, timezoneOffset));
      return false;
    }

    if (errorMessages) {
      handleSetError(errorMessages);
      return false;
    }

    newEvent.startTimeEpoch = moment(newEvent.start).startOf('minutes').valueOf();
    newEvent.duration = getEventDuration(newEvent.start, newEvent.stop);

    return true;
  };

  /**
   * Applies the chosen type to every selected strip and stages the result in the report store. Validates and prepares each event in parallel, skipping those that fail the overlap check, the AFIB five-second minimum or the three-beat minimum, folds the survivors into accumulated change and new lists, writes those to event options, event changes and event news, then closes the dialog and drops the loading flag.
   *
   * @returns A promise resolving once every selected strip has been validated and the staged changes written to the store.
   */
  const onClickChangeEventType = async () => {
    setIsLoading(true);
    let accumulatedEChange: any[] = [];
    let accumulatedENew: any[] = [];

    try {
      /**
       * Builds the retyped copy of one selected strip and rejects it when the new type is not permitted. Preserves the original type in oldType, runs the full overlap and data-range check, then rejects with a toast for an AFIB shorter than five seconds or for a run or three-beat-minimum type carrying fewer than three beats.
       *
       * @param event - The selected strip to retype.
       * @returns A promise resolving to the prepared event, or to null when a validation rule rejected it.
       */
      const validateAndPrepareEvent = async (event: any) => {
        const updatedEvent = {
          ...event,
          type: state.eventType.value,
          oldType: event.oldType || event.type,
        };
        const isValidChange = await checkChangeTypeEvent(updatedEvent);

        if (!isValidChange) {
          return null;
        }

        if (
          state.eventType.value === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
          getEventDurationInSecond(updatedEvent.start, updatedEvent.stop) < 5
        ) {
          toastrError(ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE, 'Error');
          return null;
        }

        if (
          [TYPE_BEAT_EVENT_ENUM.SVE_RUN, TYPE_BEAT_EVENT_ENUM.VE_RUN].includes(
            state.eventType.value,
          ) &&
          updatedEvent.countBeats < 3
        ) {
          toastrError(
            CHANGE_EVENT_TYPE_RUN_UNDER_3_BEATS_ERROR_MESSAGE,
            'Unable to change event type',
          );
          return null;
        }

        if (minumum3BeatsType.includes(state.eventType.value) && updatedEvent.countBeats < 3) {
          toastrError(ADD_EVENT_UNDER_3_BEATS_ERROR_MESSAGE, 'Error');
          return null;
        }

        return updatedEvent;
      };

      const events =
        selectedMultipleStrip.length > 0
          ? _.uniqBy([...selectedMultipleStrip, selectedStrip].filter(Boolean), 'id')
          : [selectedStrip].filter(Boolean);
      const updatedEvents = (await Promise.all(events.map(validateAndPrepareEvent))).filter(
        Boolean,
      );

      accumulatedEChange = getEventChanges() || [];
      accumulatedENew = getEventNews() || [];

      _.forEach(updatedEvents, (event) => {
        const { eChange, eNew } = updateEventChangeType(
          state.eventType,
          event.oldType,
          event,
          accumulatedENew,
          accumulatedEChange,
        );
        accumulatedEChange = eChange;
        accumulatedENew = eNew;
      });

      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventChangesState: accumulatedEChange,
        eventNewsState: accumulatedENew,
      }));
      setEventChanges(accumulatedEChange);
      setEventNews(accumulatedENew);
      accumulatedEChange = [];
      accumulatedENew = [];
      onClickCancel();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Narrows the change-event-type dropdown to the options legal for the tab the user is on. Drops AVB2 and AVB3 while the active tab is SVE_RUN or VE_RUN, drops SVE_RUN and VE_RUN when the active tab is not in the run-event allow list, and otherwise hands back the full option list.
   *
   * @returns The filtered list of change-event-type options.
   */
  const getAvailableChangeEventTypeOptions = () => {
    const currentType = getActiveTab();

    // Filter out AVB2 and AVB3 if current type is SVE_RUN or VE_RUN
    if (
      currentType === TYPE_BEAT_EVENT_ENUM.SVE_RUN ||
      currentType === TYPE_BEAT_EVENT_ENUM.VE_RUN
    ) {
      return CHANGE_EVENT_TYPE_OPTIONS.filter(
        (opt) =>
          opt.value !== TYPE_RHYTHM_EVENT_ENUM.AVB2 && opt.value !== TYPE_RHYTHM_EVENT_ENUM.AVB3,
      );
    }

    // Filter out SVE_RUN and VE_RUN if current type is not allowed for run events
    // activeTabState also allows TYPE_BEAT_EVENT_ENUM/TBeatHrActiveTab, wider than this array's TYPE_RHYTHM_EVENT_ENUM element type.
    if (!RUN_EVENT_CHANGE_ALLOWED_TYPES.includes(currentType as TYPE_RHYTHM_EVENT_ENUM)) {
      return CHANGE_EVENT_TYPE_OPTIONS.filter(
        (opt) =>
          opt.value !== TYPE_BEAT_EVENT_ENUM.SVE_RUN && opt.value !== TYPE_BEAT_EVENT_ENUM.VE_RUN,
      );
    }

    // Return all options by default
    return CHANGE_EVENT_TYPE_OPTIONS;
  };

  return {
    state,
    isLoading,
    onChangeEvent,
    onClickCancel,
    onClickChangeEventType,
    getAvailableChangeEventTypeOptions,
  };
};

export default useChangeEventType;
